// Import react elements
import React, { useState, useEffect } from 'react';

// Import hooks
import { usePersistentFilters } from 'global/hooks/usePersistentFilters';


// Import components
import CustomisableTable from 'global/components/CustomisableTable';
import CreateButton from "global/components/CreateButton";
import CreateToggleFilters from 'global/components/CreateToggleFilters';



const TransactionsTable = ({ initialTableColumns, transactionsData, rowKeyPrefix, loadingTransactions, uniqueFilterOptions, defaultFilters, fetchTransactionsByDateRange, handleSelectTransaction }) => {


    // Set filters state - needs passing to filters component and table component
    const [filters, setFilters] = useState({});


    // State needed for CreateCheckboxFilters
    const [selectedRows, setSelectedRows] = useState([]);
    

    // Your filter keys you want to persist
    const filterKeys = ['type'];


    // This object maps internal filter keys to user-friendly labels
    const filterLabels = {
        type: 'Transaction Type',
    };


    // Extract form-related state and functions from the `usePersistentFilters` custom hook  (for orders table filters)
    const {
        selectedFilters,
        toggleFilterValue,
        setFilterGroupAllOn,
        setFilterGroupAllOff,
        resetFilterGroup
    } = usePersistentFilters("transactionsTable", filterKeys, defaultFilters);



    useEffect(() => {

        // Whenever selectedFilters changes, apply them to the table filters
        setFilters((prev) => ({
            ...prev,
            ...selectedFilters,
        }));

    }, [selectedFilters]);



    // Set today's date
    const today = new Date().toISOString().split(" ")[0];

    // Date range filter state
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');



    // Function to handle date filter submit button click
    const handleDateFilterSubmit = async (startDate, endDate) => {

        // If there is a start data
        if (startDate) {

            // If the start date is 'clear'
            if (startDate === "clear") {

                // Clear the start date 
                setFilterStartDate(null);

            } else {

                // Set the start date
                setFilterStartDate(startDate);

            }

        }

        // If there is a end data
        if (endDate) {

            // If the end date is 'clear'
            if (endDate === "clear") {

                // Clear the end date 
                setFilterEndDate(null);

            } else {

                // Set the end date
                setFilterEndDate(endDate)

            }
            
        }

        // Set the start and end date - if the date passed in is 'clear' set it as null, otherwise set it, if it's not passed in use the state else leave as null
        const fromDate = startDate ? startDate === "clear" ? null : startDate + " 00:00:00" : filterStartDate ? filterStartDate + " 00:00:00" : null;
        const toDate = endDate ? endDate === "clear" ? null : endDate + " 00:00:00" : filterEndDate ? filterEndDate + " 00:00:00" : null;
        
        // Fetch the orders by giving the dates to the api
        fetchTransactionsByDateRange(fromDate, toDate);

    };



    // Handle clearing the date pickers when they have a value
    const handleClearDatePickers = () => {

        // Clear the start date
        setFilterStartDate(null);

        // Clear the end date
        setFilterEndDate(null);

        // Fetch the transactions with no start or end date
        fetchTransactionsByDateRange(null, null);

    }



    const renderDatePickers = () => {

        return (

            <div className="date_range_picker_container">

                <div className="filters__date-inputs">

                    {/* <p className="date_range_picker_title">View all earnings between:</p> */}

                    <label className="filters__date-label">

                        <input
                            title="View Earnings From"
                            type="date"
                            max={filterEndDate || today}
                            value={filterStartDate || ""}
                            onChange={(e) => {
                            
                                if (!e.target.value) {

                                    // Fetch the transactions with the date but clear date if the user cleared the date picker
                                    handleDateFilterSubmit("clear", null);

                                    return;

                                }

                                // Fetch the transactions with the date
                                handleDateFilterSubmit(e.target.value, null);

                            }}
                            style={{ 
                                marginRight: "10px", 
                                border: filterStartDate ? "solid 1px var(--primary-color)" : "", 
                                background: filterStartDate ? "var(--primary-color-trans)" : "",
                            }}
                        />
                    </label>

                    -

                    <label className="filters__date-label">

                        <input
                            title="View Earnings To"
                            type="date"
                            min={filterStartDate || ""}
                            value={filterEndDate || ""}
                            onChange={(e) => {
                            
                                if (!e.target.value) {

                                    // Fetch the transactions with the date but clear date if the user cleared the date picker
                                    handleDateFilterSubmit(null, "clear");

                                    return;

                                }

                                // Fetch the transactions with the date
                                handleDateFilterSubmit(null, e.target.value);

                            }}
                            style={{ 
                                marginLeft: "10px", 
                                marginRight: "10px", 
                                border: filterEndDate ? "solid 1px var(--primary-color)" : "",
                                background: filterEndDate ? "var(--primary-color-trans)" : "",
                            }}
                        />
                    </label>


                    <CreateButton
                        title="Clear"
                        size="s"
                        invert={true}
                        type="button"
                        onClick={handleClearDatePickers}
                        disabled={!filterStartDate && !filterEndDate}
                    />

                </div>

            </div>

        )
    };



    return (

        <div style={{ overflowX: 'auto', minWidth: '100%' }}>

            {/* If there is a function to fetch between dates then show the date pickers */}
            {fetchTransactionsByDateRange &&

                <div className="options_banner">

                    <div className="date_range_picker">

                        {renderDatePickers()}

                    </div>

                </div>

            }



            <div className="left_checkbox_filters_div">

                {/* 
                <CreateCheckboxFilters
                    uniqueFilterOptions={uniqueFilterOptions}
                    selectedFilters={selectedFilters}
                    filterLabels={filterLabels}
                    clearFilterGroup={clearFilterGroup}
                    toggleFilterValue={toggleFilterValue}
                    setSelectedRows={setSelectedRows}
                />
                */}

                <CreateToggleFilters 

                    // This contains all unique filter options grouped by key
                    uniqueFilterOptions={uniqueFilterOptions}

                    // This contains the currently included values for each filter group
                    selectedFilters={selectedFilters}

                    // This contains the default included values for each filter group
                    defaultFilters={defaultFilters}

                    // This contains the visible labels for each filter group
                    filterLabels={filterLabels}

                    // This toggles a single value on or off inside a filter group
                    toggleFilterValue={toggleFilterValue}

                    // This sets all values in a filter group to included
                    setFilterGroupAllOn={setFilterGroupAllOn}

                    // This sets all values in a filter group to excluded
                    setFilterGroupAllOff={setFilterGroupAllOff}

                    // This resets a filter group back to its default included values
                    resetFilterGroup={resetFilterGroup}

                    // So we can clear any selected table rows when changing filters
                    setSelectedRows={setSelectedRows}

                />

            </div>

            <div className="right_table_div">

                <CustomisableTable 
                    tableData={transactionsData} 
                    initialTableColumns={initialTableColumns} 
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}
                    handleSelectRow={handleSelectTransaction}
                    handleRowSingleClick={handleSelectTransaction} 
                    filterKeys={filterKeys} 
                    filters={filters} 
                    setFilters={setFilters} 
                    loadingData={loadingTransactions}
                    rowKeyPrefix={rowKeyPrefix}
                    noDataAvaiableMessage={ 

                        (!uniqueFilterOptions || Object.keys(uniqueFilterOptions).length === 0) ? 

                            "No transactions to display." 

                            : "There are no transactions matching your selected filters." 

                    }
                />

            </div>

        </div>



    )



};

export default TransactionsTable;
